import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import type { TaskConfigSettings } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { tasksService } from '@/services/tasks.service';
import { getErrorMessage } from '@/lib/errors';
import { useConfigSettings } from './useConfigSettings';
import { ConfigTabShell } from './ConfigTabShell';

// The recurrence engine computes every date in IST, matching Attendance and Payroll.
// Offering other zones here would be a setting that silently does nothing.
const TIMEZONES = ['Asia/Kolkata'];

function Form({ initial, save, saving }: { initial: TaskConfigSettings; save: (p: Partial<TaskConfigSettings>) => void; saving: boolean }) {
  const [resetTime, setResetTime] = useState(initial.reset_time);
  const [tz, setTz] = useState(initial.reset_timezone);
  const dirty = resetTime !== initial.reset_time || tz !== initial.reset_timezone;

  const runNow = useMutation({
    mutationFn: () => tasksService.runDailyRoll(),
    onSuccess: (r) => toast.success(
      `Created ${r.occurrences_created} occurrence(s) · closed ${r.auto_closed} as discarded · archived ${r.archived}`,
    ),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card className="max-w-lg" variant="flat">
      <CardHeader><CardTitle>Reset Cycle</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>Reset time</Label>
          <TimePicker value={resetTime} onChange={setResetTime} />
          <p className="text-xs text-muted-foreground">Time of day recurring tasks roll forward to their next occurrence.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Timezone</Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">The task engine operates in IST.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={!dirty || saving} onClick={() => save({ reset_time: resetTime, reset_timezone: tz })}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" disabled={runNow.isPending} onClick={() => runNow.mutate()}>
            <RefreshCw className="h-4 w-4" /> {runNow.isPending ? 'Running…' : 'Run now'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The engine checks every 5 minutes and rolls tasks forward once the reset time has passed.
          Use Run now to trigger a pass immediately.
        </p>
      </CardContent>
    </Card>
  );
}

export function ResetCycleTab() {
  const { data, isLoading, isError, refetch, save, saving } = useConfigSettings();
  return (
    <ConfigTabShell isLoading={isLoading} isError={isError} onRetry={refetch}>
      {data && <Form initial={data} save={save} saving={saving} />}
    </ConfigTabShell>
  );
}
